import { Component, Input, OnChanges, OnInit, ViewChild } from '@angular/core';
import { ProfileReferralService } from '../shared/profile-referral.service';
import { ReferralValueReportSearchDto } from '../shared/dto/referral-value-report-search.dto';
import { ReferralGraphDataPoint, ReferralValueReportDto } from '../shared/dto/referral-value-report.dto';
import { ActivatedRoute } from '@angular/router';
import { ChartData } from 'src/app/shared/chart/chart-data.model';
import { ChartService } from 'src/app/shared/chart/chart.service';
import { UIChart } from 'primeng/chart';

@Component({
  selector: 'wl-profile-referral-value-report',
  templateUrl: './profile-referral-value-report.component.html',
  styleUrls: ['./profile-referral-value-report.component.less'],
  providers: [ChartService],
    standalone: false
})
export class ProfileReferralValueReportComponent implements OnInit, OnChanges {
  @Input() public profileId: number;
  @ViewChild('byTypeGraph', { static: false }) public byTypeGraph: UIChart;
  @ViewChild('monthlyGraph', { static: false }) public monthlyGraph: UIChart;
  referralValueReportSearch: ReferralValueReportSearchDto;
  referralValueReport: ReferralValueReportDto;
  includeBots: boolean;
  isLoading: boolean
  startDate: string;
  endDate: string;

  // Referrals field
  totalReferrals: number;
  totalImpressions: number;
  totalLeads: number;
  leadRate: number;
  voidStatement: string;

  // Graphs Value
  monthlyGraphData: ChartData;
  monthlyGraphOptions: any;
  monthlyGraphColors: Array<string>;
  byTypeGraphData: ChartData;
  byTypeGraphOptions: any;
  byTypeGraphColors: Array<string>;

  // VOI Fields
  voiStatement: string;
  voiNote: string;
  voiEnabled: boolean;
  voiThreshold: number;
  investment: number;
  periodInvestment: number;
  valueOfReferrals: number;
  valueRate: number;
  showVoi: boolean;

  // Thresholds for class
  refOkayReq: number = 50;
  leadRateOkayReq: number = 0.01;
  leadRateGoodReq: number = 0.02;

  constructor(
    private _profileReferralService: ProfileReferralService,
    private _route: ActivatedRoute,
    private _chartService: ChartService
  ) {
    this.isLoading = true;
    this.includeBots = false;
  }

  ngOnInit(): void {
    this._route.params.subscribe((id) => {
      this.profileId = parseInt(id['profileId'], 10);
    });
    this.includeBots = false;
    let today = new Date();
    today.setHours(0, 0, 0, 0);
    let aYearAgo = new Date();
    aYearAgo.setFullYear((today.getFullYear() - 1));

    this.referralValueReportSearch = {
      ProfileId: this.profileId,
      StartDate: aYearAgo,
      EndDate: today,
      ReferralTypes: null
    }
    this.monthlyGraphData = new ChartData();
    this.byTypeGraphData = new ChartData();
    this.fetchResults();
  }

  ngOnChanges(): void {
    console.log(this.includeBots)
  }

  public fetchResults(): void {
    this.isLoading = true;
    this._profileReferralService
      .getRefferalValueReport(this.referralValueReportSearch)
      .then(result => {
        this.includeBots = false;
        this.referralValueReport = result as ReferralValueReportDto;
        this.assignFields();
        this.isLoading = false;
      });
  }

  public includeBotsChange(): void {
    this.assignFields();
  }

  private formatDate(d: Date): string {
    let date = new Date(d);
    let x = date.toDateString();
    let y = x.split(" ");
    y.shift();
    return y.join(" ")
  }

  private assignFields(): void {
    this.startDate = this.formatDate(this.referralValueReport.StartDate);
    this.endDate = this.formatDate(this.referralValueReport.EndDate);
    this.investment = this.referralValueReport.Investment;
    this.periodInvestment = this.referralValueReport.PeriodInvestment;
    this.voiEnabled = this.referralValueReport.VOIEnabled;
    this.voiThreshold = this.referralValueReport.VOIThreshold;
    this.voiStatement = this.referralValueReport.VOIText;

    if (!this.includeBots) {
      this.totalImpressions = this.referralValueReport.TotalImpressions - this.referralValueReport.TotalImpressionBots;
      this.totalLeads = this.referralValueReport.TotalLeads - this.referralValueReport.TotalLeadsBots;
      this.totalReferrals = this.referralValueReport.TotalReferrals - this.referralValueReport.TotalReferralBots;
      this.setMonthlyGraphData(this.referralValueReport.ReferralsPerMonth);
      this.setPerTypeGraphData(this.referralValueReport.ReferralsPerType);
      this.valueOfReferrals = this.referralValueReport.ValueOfReferrals;
    } else {
      this.totalImpressions = this.referralValueReport.TotalImpressions;
      this.totalLeads = this.referralValueReport.TotalLeads;
      this.totalReferrals = this.referralValueReport.TotalReferrals;
      this.setMonthlyGraphData(this.referralValueReport.ReferralsPerMonthWithBots);
      this.setPerTypeGraphData(this.referralValueReport.ReferralsPerTypeWithBots);
      this.valueOfReferrals = this.referralValueReport.ValueOfReferralsWithBots;
    }

    this.leadRate = (this.totalLeads / this.totalImpressions);
    this.valueRate = (this.valueOfReferrals > 0 && this.periodInvestment > 0) ? Number((this.valueOfReferrals / this.periodInvestment).toFixed(2)) : 0;
    this.showVoi = (this.voiEnabled && ((this.valueRate * 100) >= this.voiThreshold));
    this.voiNote = this.buildVoiNote();
  }

  private buildVoiNote(): string {
    return `Based on your annual dues investment of $${this.investment}, 
    this value represents ${this.valueRate * 100}% of your $${this.periodInvestment} investment for the time 
    period of ${this.startDate} - ${this.endDate}.`
  }

  private setMonthlyGraphData(kvp: ReferralGraphDataPoint<string, number>[]): void {
    this.monthlyGraphData = new ChartData();
    this.monthlyGraphOptions = {
      responsive: false,
      plugins: {
        legend: {
          position: true,
        },
      }
    };

    kvp.sort(function (a, b) {
      return (new Date(a.Key).getTime()) - (new Date(b.Key).getTime())
    });

    for (let kv of kvp) {
      this.monthlyGraphData.labels.push(this.getMonthLabel(kv.Key));
      this.monthlyGraphData.datasets[0].data.push(kv.Value);
    }

    this.monthlyGraphColors = this._chartService.getChartColors(kvp.length);
    this.monthlyGraphData.datasets[0].backgroundColor = this.monthlyGraphColors
    this.monthlyGraph.refresh();;
  }

  private setPerTypeGraphData(kvp: ReferralGraphDataPoint<string, number>[]): void {
    this.byTypeGraphData = new ChartData();
    this.byTypeGraphOptions = {
      responsive: false,
      plugins: {
        legend: {
          position: true,
        },
      },
    };
    for (let kv of kvp) {
      this.byTypeGraphData.labels.push(this.getReferralTypeLabel(kv.Key));
      this.byTypeGraphData.datasets[0].data.push(kv.Value);
    }
    this.byTypeGraphColors = this._chartService.getChartColors(kvp.length);
    this.byTypeGraphData.datasets[0].backgroundColor = this.byTypeGraphColors
    this.byTypeGraph.refresh();
  }

  private getMonthLabel(key: string): string {
    let x = (new Date(key)).toDateString();
    let y = x.split(" ");
    return y[1] + " " + y[3];
  }

  private getReferralTypeLabel(key: string): string {

    const names = {
      "AC": "Banner Ad Click",
      "AD": "Banner Ad Display",
      "BD": "Business Connect Detail View",
      "BV": "Business Connect Request Listing View",
      "CD": "Coupon Detail View",
      "CV": "Coupon List View",
      "D": "Directory Display",
      "DV": "Daily Vacancy Referral",
      "E": "Email Sent",
      "ED": "Event Detail Displayed",
      "EM": "Event Reminder Set",
      "ER": "Event Registration Submitted",
      "EV": "Event Registration Viewed",
      "F": "Front Desk Referral",
      "ML": "Members Only Login",
      "M": "Map Referral",
      "ND": "News Display",
      "SC": "Sponsor Image Click",
      "SD": "Sponsor Image Display",
      "SM": "Sponsor Message Display",
      "W": "Website Redirect",
      "_D": "Web Content Display",
      "LV": "Listing Detail Page Display",
      "LD": "Listing Details Displayed",
      "LW": "Listing Detail Website Click",
      "LE": "Listing Detail Email Submit",
      "SN": "Generic Social Network Redirect",
      "SNFB": "Facebook Social Network Redirect",
      "SNTW": "Twitter Social Network Redirect",
      "SNLI": "LinkedIn Social Network Redirect",
      "SNPN": "Pinterest Social Network Redirect",
      "SNBL": "Blogger Social Network Redirect",
      "RSS": "RSS Feed Redirect",
      "SNYT": "YouTube Social Network Redirect",
      "SNFL": "FlickR Social Network Redirect",
      "SNPC": "Picasa Social Network Redirect",
      "SNYP": "Yelp Social Network Redirect",
      "SNOT": "Open Table Social Network Redirect",
      "SNGP": "Google Plus Social Network Redirect",
      "SNIG": "Instagram Social Network Redirect",
      "SNFS": "FourSquare Social Network Redirect",
      "SNHO": "Houzz Social Network Redirect",
      "SNAL": "Angie's List Social Network Redirect",
      "SNTA": "TripAdvisor Social Network Redirect",
    }

    return names[key] ?? key;
  }
}

